import React, { useEffect, useRef, useState } from "react";
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, LogOut, ChevronDown, Menu, X, UserCircle, BookOpen } from "lucide-react";
import { AlertsBell } from "./AlertsBell";
import { useAuth } from "../../lib/auth";
import { api, logoUrl, avatarUrl } from "../../lib/api";
import { usePlatformBranding, platformLogoUrl, PoweredByFooter, isLightColor, applyTheme } from "../../lib/branding";
import { cn } from "../../lib/utils";
import type { Vehicle } from "../../lib/types";
import { BO_NAV, CLIENT_NAV, STAFF_NAV, OPERATOR_HIDDEN_SUBS, STAFF_HIDDEN_SUBS, OPERATOR_HIDDEN_MAIN, mainActive, subActive } from "./nav";
import { SupportWidget } from "../support/SupportWidget";

function GlobalSearch({ base }: { base: string }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data } = useQuery<{ items: Vehicle[] }>({
    queryKey: ["global-search", q],
    queryFn: async () => (await api.get("/vehicles", { params: { q, limit: 6 } })).data,
    enabled: q.trim().length >= 2,
  });
  return (
    <div className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute left-3.5 top-2.5 h-4 w-4 text-white/60" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onFocus={() => setOpen(true)}
        placeholder="Search vehicles by plate, make, model…"
        className="h-10 w-full rounded-full border border-white/15 bg-white/10 pl-10 pr-4 text-sm text-white placeholder:text-white/55 focus:bg-white/15 focus:outline-none"
      />
      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-12 z-50 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl" data-testid="global-search-results">
          {(data?.items || []).map((v) => (
            <button key={v.id} onMouseDown={() => { navigate(`${base}/vehicles/${v.id}`); setQ(""); }} className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50">
              <span className="plate">{v.plate_number}</span><span className="text-slate-800">{v.make} {v.model}</span><span className="ml-auto text-xs text-slate-400">{v.tenant_name}</span>
            </button>
          ))}
          {data && data.items.length === 0 && <p className="px-4 py-3 text-sm text-slate-500">No vehicles match.</p>}
        </div>
      )}
    </div>
  );
}

export function AppShell({ portal }: { portal: "backoffice" | "client" }) {
  const { user, tenant, logout, hasModule } = useAuth();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const isBO = portal === "backoffice";
  const base = isBO ? "/bo" : "/app";
  const { data: pb } = usePlatformBranding();
  const brand = isBO ? "#10046E" : pb?.primary_color || "#10046E";
  const light = isLightColor(brand);
  const fg = light ? "#0f172a" : "#ffffff";
  const secondary = isBO ? "#EEBD0A" : pb?.secondary_color || "#EEBD0A";
  const accent = isBO ? "var(--gold)" : isLightColor(secondary) === light ? (light ? "#10046E" : "#ffffff") : secondary;
  const navFg = isBO ? "#ffffff" : isLightColor(secondary) ? "#0f172a" : "#ffffff";
  const operatorLogo = pb?.portal_logo_file_id ? platformLogoUrl("portal", pb.portal_logo_file_id) : "/enfleet-logo.png";
  const role = user?.role || "admin";
  const nav = (isBO ? BO_NAV : CLIENT_NAV).filter((n) => (!n.module || hasModule(n.module)) && (isBO || role !== "staff" || STAFF_NAV.includes(n.label)) && (isBO || role !== "operator" || !OPERATOR_HIDDEN_MAIN.includes(n.label)));
  const activeMain = nav.find((n) => mainActive(n, pathname, base));
  const subs = (activeMain?.children || []).filter((s) => (!s.module || hasModule(s.module)) && (isBO || (role === "operator" ? !OPERATOR_HIDDEN_SUBS.includes(s.label) : role === "staff" ? !STAFF_HIDDEN_SUBS.includes(s.label) : true)));
  const [menu, setMenu] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [logoOk, setLogoOk] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (isBO || pb) applyTheme(isBO, pb); }, [isBO, pb]);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const onLogout = async () => { await logout(); navigate(isBO ? "/bo/login" : "/login"); };
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex min-h-screen flex-col bg-[#f4f5fa]">
      {!isBO && !pb && <div className="fixed inset-0 z-[100] bg-white" data-testid="branding-loading" />}
      <header className={cn("sticky top-0 z-40 shadow-md", light && "nav-light")} style={{ background: brand, color: fg }} data-testid="top-header">
        <div className="flex h-14 items-center gap-4 px-4 sm:px-6">
          <button data-testid="mobile-menu-button" className="rounded p-1 lg:hidden" onClick={() => setMobile(!mobile)}>{mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
          <NavLink to={base} className="flex shrink-0 items-center gap-3" data-testid="header-logo-link">
            {!isBO && tenant?.branding?.logo_file_id && logoOk ? (
              <>
                <img src={operatorLogo} alt={pb?.company_name || "ENFLEET by POMEN"} className="hidden h-7 max-w-[120px] object-contain sm:block" data-testid="operator-portal-logo" />
                <span className="hidden h-6 w-px sm:block" style={{ opacity: 0.3, background: fg }} />
                <img src={logoUrl(tenant.id, tenant.branding.logo_file_id)} alt={tenant.name} className="h-9 max-w-[140px] rounded bg-white/95 object-contain px-1.5 py-0.5" onError={() => setLogoOk(false)} data-testid="tenant-logo" />
              </>
            ) : (
              <img src={isBO ? "/enfleet-logo.png" : operatorLogo} alt={isBO ? "ENFLEET by POMEN" : pb?.company_name || "ENFLEET by POMEN"} className="h-8 max-w-[160px] object-contain" data-testid={isBO ? "enfleet-logo" : "operator-portal-logo"} />
            )}
          </NavLink>
          <div className="hidden flex-1 justify-center md:flex"><GlobalSearch base={base} /></div>
          <div className="ml-auto flex items-center gap-2">
            {!isBO && <span className={cn("hidden rounded-full px-3 py-1 text-xs font-medium lg:block", light ? "bg-black/5" : "bg-white/10")} data-testid="tenant-name-header">{tenant?.branding?.display_name || tenant?.name}</span>}
            {isBO && pb?.nav_logo_file_id && <img src={platformLogoUrl("nav", pb.nav_logo_file_id)} alt={pb.company_name} className="hidden h-9 max-w-[150px] rounded bg-white/95 object-contain px-2 py-0.5 lg:block" data-testid="operator-nav-logo" />}
            {isBO && <span className="hidden rounded-full bg-[color:var(--gold)] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-900 lg:block" data-testid="portal-badge">{pb?.company_name && pb.company_name !== "POMEN" ? `${pb.company_name} · HQ` : "HQ Master Admin"}</span>}
            <AlertsBell base={base} light={light} accent={accent} brand={brand} enabled={hasModule("alerts")} />
            <div className="relative" ref={menuRef}>
              <button data-testid="user-menu-button" onClick={() => setMenu(!menu)} className={cn("flex items-center gap-1.5 rounded-full py-1 pl-1 pr-2", light ? "hover:bg-black/5" : "hover:bg-white/10")}>
                {user?.avatar_file_id ? (
                  <img src={avatarUrl(user.id, user.avatar_file_id)} alt={user.name} className="h-8 w-8 rounded-full object-cover ring-2 ring-white/80" data-testid="header-avatar-image" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold" style={{ background: fg, color: brand }}>{initials}</span>
                )}
                <ChevronDown className="h-4 w-4 opacity-70" />
              </button>
              {menu && (
                <div className="absolute right-0 top-11 w-60 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl" data-testid="user-menu">
                  <div className="border-b border-slate-100 px-4 py-3"><p className="text-sm font-semibold text-slate-900" data-testid="current-user-name">{user?.name}</p><p className="truncate text-xs text-slate-500">{user?.email}</p><p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{isBO ? (user?.role === "finance" ? "POMEN Finance" : "POMEN Super Admin") : `${role === "staff" ? "Staff" : role === "operator" ? "Manager" : "Administrator"} · ${tenant?.name}`}</p></div>
                  <Link data-testid="profile-menu-link" to={`${base}/profile`} onClick={() => setMenu(false)} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"><UserCircle className="h-4 w-4" />My Profile</Link>
                  {isBO && <Link data-testid="manual-menu-link" to="/bo/manual" onClick={() => setMenu(false)} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"><BookOpen className="h-4 w-4" />User Manual & QA Script</Link>}
                  <button data-testid="logout-button" onClick={onLogout} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"><LogOut className="h-4 w-4" />Sign out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <nav className={cn("sticky top-14 z-30 shadow-sm lg:h-11", mobile ? "block" : "hidden lg:block")} style={{ background: isBO ? brand : secondary, color: navFg, boxShadow: isBO ? undefined : `inset 0 1px 0 ${light ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.15)"}` }} data-testid="main-nav">
        <div className="flex h-full flex-col px-2 lg:flex-row lg:items-stretch lg:px-4">
          {nav.filter((n) => !n.hidden).map((n) => {
            const active = n === activeMain;
            return (
              <Link key={n.to} to={n.to} onClick={() => setMobile(false)} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={cn("main-nav-item", active && "active")}>
                {n.label}
              </Link>
            );
          })}
        </div>
      </nav>
      {subs.length > 0 && (
        <nav className={cn("liquid-glass sticky top-[6.25rem] z-20 -mt-px", mobile ? "block" : "hidden lg:block")} data-testid="sub-nav">
          <div className="flex flex-col px-2 lg:flex-row lg:items-stretch lg:px-4">
            {subs.map((s) => (
              <Link key={s.to} to={s.to} onClick={() => setMobile(false)} data-testid={`subnav-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={cn("sub-nav-item", subActive(s, pathname, search) && "active")}>
                {s.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
      <main className="relative z-0 flex-1 px-4 py-6 sm:px-6"><Outlet /></main>
      {!isBO && <SupportWidget />}
      <footer className="flex items-center justify-between px-6 pb-4 text-slate-400">
        <PoweredByFooter />
        {!isBO && <span className="text-[11px]">{tenant?.branding?.display_name || tenant?.name}</span>}
      </footer>
    </div>
  );
}
